import React, { useEffect, useRef } from "react";
import { motion, useInView, useAnimation, AnimatePresence } from "framer-motion";
// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

const Reveal = ({ children, direction, showCurtain, curtainDirection, curtainColor, width, noStyle, animateOnExit, delay, fullHeight }) => {
    const mainControls = useAnimation();
    const slideControls = useAnimation();

    const ref = useRef(null);
    const isInView = useInView(ref, {
        once: animateOnExit ? false : true,
    });

    useEffect(() => {
        if (isInView) {
            slideControls.start("visible");
            mainControls.start("visible");
        } else {
            slideControls.start("hidden");
            mainControls.start("hidden");
        }
    }, [isInView, mainControls, slideControls]);

    return (
        <div ref={ref} style={noStyle ? fullHeight ? { height: "100%" } : {} : { position: "relative", width, overflow: "hidden" }}>
            {
                direction === "inout" ? (
                    <>
                        <AnimatePresence >
                            <motion.div
                                style={fullHeight ? { height: "100%" } : {}}
                                initial={{ opacity: 0, scale: 0.5 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.5 }}
                                transition={{
                                    duration: 0.5,
                                    delay: 0,
                                    ease: [0, 0.71, 0.2, 1.01]
                                }}
                            >
                                {children}
                            </motion.div>
                        </AnimatePresence>
                    </>
                )
                    :
                    (
                        <>
                            <motion.div
                                variants={{
                                    hidden: {
                                        opacity: 0,
                                        ...(() => {
                                            switch (direction) {
                                                case "left":
                                                    return { x: -75 };
                                                case "right":
                                                    return { x: 75 };
                                                case "top":
                                                    return { y: -75 };
                                                case "bottom":
                                                    return { y: 75 };
                                                default:
                                                    return { x: -75 };
                                            }
                                        })(),
                                    },
                                    visible: {
                                        opacity: 1,
                                        ...(() => {
                                            switch (direction) {
                                                case "left":
                                                    return { x: 0 };
                                                case "right":
                                                    return { x: 0 };
                                                case "top":
                                                    return { y: 0 };
                                                case "bottom":
                                                    return { y: 0 };
                                                default:
                                                    return { x: 0 };
                                            }
                                        })(),
                                    },
                                }}
                                initial="hidden"
                                animate={mainControls}
                                transition={{ duration: 0.5, delay: delay }}
                            >
                                {children}
                            </motion.div>
                            {
                                showCurtain &&
                                <motion.div
                                    variants={{
                                        hidden: {
                                            ...(() => {
                                                switch (curtainDirection) {
                                                    case "left":
                                                        return { left: 0 };
                                                    case "right":
                                                        return { right: 0 };
                                                    case "top":
                                                        return { top: 0 };
                                                    case "bottom":
                                                        return { bottom: 0 };
                                                    default:
                                                        return { left: 0 };
                                                }
                                            }
                                            )(),
                                        },
                                        visible: {
                                            ...(() => {
                                                switch (curtainDirection) {
                                                    case "left":
                                                        return { left: "100%" };
                                                    case "right":
                                                        return { right: "100%" };
                                                    case "top":
                                                        return { top: "100%" };
                                                    case "bottom":
                                                        return { bottom: "100%" };
                                                    default:
                                                        return { left: "100%" };
                                                }
                                            })(),
                                        },
                                    }}
                                    initial="hidden"
                                    animate={slideControls}
                                    transition={{ duration: 0.5, ease: "easeIn" }}
                                    style={{
                                        position: "absolute",
                                        top: 4,
                                        bottom: 4,
                                        left: 0,
                                        right: 0,
                                        background: curtainColor,
                                        zIndex: 20,
                                    }}
                                />
                            }
                        </>
                    )
            }
        </div>
    );
};

Reveal.propTypes = {
    children: PropTypes.node.isRequired,
    direction: PropTypes.oneOf(["left", "right", "top", "bottom", "inout"]),
    width: PropTypes.string,
    showCurtain: PropTypes.bool,
    curtainDirection: PropTypes.oneOf(["left", "right", "top", "bottom"]),
    curtainColor: PropTypes.string,
    animateOnExit: PropTypes.bool,
    noStyle: PropTypes.bool,
    delay: PropTypes.number,
    fullHeight: PropTypes.bool
}

Reveal.defaultProps = {
    direction: "bottom",
    width: "fit-content",
    showCurtain: false,
    curtainDirection: "left",
    curtainColor: "#89CFF0",
    animateOnExit: false,
    noStyle: false,
    delay: 0.25,
    fullHeight: false
}

export default Reveal;